<template>
  <div class="stop-groups">
    <div class="stop-groups__bar">
      <v-menu offset-y max-height="420">
        <template v-slot:activator="{ on }">
          <button
            type="button"
            class="stop-groups__bus"
            :style="{ borderLeftColor: selectedBus ? selectedBus.color : '#bdbdbd' }"
            v-on="on"
          >
            <template v-if="selectedBus">
              <span class="stop-groups__bus-title">{{ selectedBus.title }}</span>
              <span v-if="selectedBus.subtitle" class="stop-groups__bus-sub">
                {{ selectedBus.subtitle }}
              </span>
            </template>
            <span v-else class="stop-groups__bus-sub">Select a bus</span>
            <v-icon small class="ml-auto">mdi-menu-down</v-icon>
          </button>
        </template>
        <v-list dense class="stop-groups__bus-list">
          <v-list-tile
            v-for="b in buses"
            :key="b.id"
            :class="{ 'stop-groups__bus-item--active': b.id === busId }"
            @click="$emit('update:busId', b.id)"
          >
            <v-list-tile-action class="stop-groups__bus-item-dot">
              <span class="stop-groups__dot" :style="{ background: b.color }"></span>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title class="font-weight-medium">{{ b.title }}</v-list-tile-title>
              <v-list-tile-sub-title v-if="b.subtitle">{{ b.subtitle }}</v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
        </v-list>
      </v-menu>

      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        placeholder="Highlight student…"
        solo
        flat
        hide-details
        clearable
        dense
        class="stop-groups__search"
      ></v-text-field>

      <v-btn small outline class="ma-0" :disabled="!busId" @click="$emit('add')">
        <v-icon small left>mdi-plus</v-icon>Add empty stop
      </v-btn>

      <v-spacer></v-spacer>
      <span v-if="saving" class="caption grey--text mr-2">Saving…</span>
      <span v-if="busId" class="caption grey--text">
        {{ stops.length }} stop{{ stops.length === 1 ? "" : "s" }} ·
        {{ students.length }} student{{ students.length === 1 ? "" : "s" }}
      </span>
    </div>

    <div v-if="!busId" class="stop-groups__empty">
      <v-icon large color="grey lighten-1">mdi-bus-stop</v-icon>
      <div class="mt-2">Pick a bus to arrange its students into stops.</div>
    </div>

    <template v-else>
      <div class="stop-groups__hint caption grey--text">
        Drag students between stops. Drag a stop by its
        <v-icon x-small>mdi-drag</v-icon> handle to change its number.
      </div>

      <div class="stop-groups__grid">
        <div
          class="stop-card stop-card--none"
          :class="{ 'stop-card--warn': unassigned.length }"
        >
          <div class="stop-card__head">
            <span class="stop-card__badge stop-card__badge--none">–</span>
            <span class="stop-card__title">No stop</span>
            <v-spacer></v-spacer>
            <span class="stop-card__count">{{ unassigned.length }}</span>
          </div>
          <draggable
            :list="unassigned"
            group="stop-students"
            :empty-insert-threshold="40"
            class="stop-card__body"
            @change="onStudentChange(null, $event)"
          >
            <div
              v-for="s in unassigned"
              :key="s.studentID"
              class="stop-student"
              :class="{ 'stop-student--match': isMatch(s) }"
            >
              <div class="stop-student__name">{{ s.fullName }}</div>
              <div class="stop-student__meta">
                {{ s.class }}-{{ s.section }} · {{ s.busHouse }} {{ s.busStreet }}
              </div>
            </div>
          </draggable>
        </div>

        <draggable
          :list="groups"
          group="stop-cards"
          handle=".stop-card__handle"
          class="stop-groups__cards"
          @change="onStopOrderChange"
        >
          <div v-for="g in groups" :key="g.key" class="stop-card">
            <div class="stop-card__head">
              <v-icon small class="stop-card__handle" title="Drag to change stop order">mdi-drag</v-icon>
              <span class="stop-card__badge" :style="{ background: color }">{{ g.stopNumber }}</span>
              <input
                class="stop-card__name"
                :value="g.name"
                placeholder="Stop name (e.g. Lee & Hewes)"
                maxlength="150"
                @change="$emit('rename', { key: g.key, name: $event.target.value })"
                @keyup.enter="$event.target.blur()"
              />
              <span class="stop-card__count">{{ g.students.length }}</span>
              <v-menu offset-y left>
                <template v-slot:activator="{ on }">
                  <v-btn icon small class="ma-0" v-on="on">
                    <v-icon small>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-tile :disabled="!g.hasLocation" @click="$emit('show-on-map', g.key)">
                    <v-list-tile-title>Show on map</v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile @click="$emit('remove', g.key)">
                    <v-list-tile-title class="error--text">Delete stop</v-list-tile-title>
                  </v-list-tile>
                </v-list>
              </v-menu>
            </div>
            <div class="stop-card__sub caption grey--text">
              {{ g.mode === "POINT" ? "Placed on map" : "Generated from line" }}
              <span v-if="g.mode === 'POINT' && g.radiusFeet"> · {{ g.radiusFeet }} ft</span>
              <span v-if="!g.hasLocation"> · no map location</span>
            </div>
            <draggable
              :list="g.students"
              group="stop-students"
              :empty-insert-threshold="40"
              class="stop-card__body"
              @change="onStudentChange(g.key, $event)"
            >
              <div
                v-for="s in g.students"
                :key="s.studentID"
                class="stop-student"
                :class="{ 'stop-student--match': isMatch(s) }"
              >
                <div class="stop-student__name">{{ s.fullName }}</div>
                <div class="stop-student__meta">
                  {{ s.class }}-{{ s.section }} · {{ s.busHouse }} {{ s.busStreet }}
                </div>
              </div>
            </draggable>
          </div>
        </draggable>
      </div>
    </template>
  </div>
</template>

<script>
import draggable from "vuedraggable";

export default {
  name: "RouteDesignStopGroups",
  components: { draggable },
  props: {
    buses: { type: Array, default: () => [] },
    busId: { type: Number, default: null },
    stops: { type: Array, default: () => [] },
    students: { type: Array, default: () => [] },
    color: { type: String, default: "#1976d2" },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      groups: [],
      unassigned: [],
      search: "",
    };
  },
  computed: {
    selectedBus() {
      return this.buses.find((b) => b.id === this.busId) || null;
    },
  },
  watch: {
    stops: { handler: "rebuild", immediate: true },
    students: "rebuild",
  },
  methods: {
    rebuild() {
      const byKey = {};
      this.groups = this.stops
        .slice()
        .sort((a, b) => a.stopNumber - b.stopNumber)
        .map((s) => {
          const g = {
            key: s.key,
            stopNumber: s.stopNumber,
            name: s.name || "",
            mode: s.mode,
            radiusFeet: s.radiusFeet,
            hasLocation: s.lat != null && s.lng != null,
            students: [],
          };
          byKey[s.key] = g;
          return g;
        });
      this.unassigned = [];
      this.students.forEach((s) => {
        const g = s.stopKey && byKey[s.stopKey];
        (g ? g.students : this.unassigned).push(s);
      });
    },
    onStudentChange(stopKey, evt) {
      if (evt.added) {
        this.$emit("move", { studentID: evt.added.element.studentID, stopKey });
      }
    },
    onStopOrderChange(evt) {
      if (evt.moved) {
        this.$emit("reorder", this.groups.map((g) => g.key));
      }
    },
    isMatch(s) {
      const q = (this.search || "").trim().toLowerCase();
      if (!q) return false;
      return (
        (s.fullName || "").toLowerCase().indexOf(q) !== -1 ||
        (s.engName || "").toLowerCase().indexOf(q) !== -1 ||
        (s.studentID || "").toLowerCase().indexOf(q) !== -1
      );
    },
  },
};
</script>

<style scoped>
.stop-groups__bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.stop-groups__bus {
  display: flex;
  align-items: baseline;
  gap: 8px;
  width: 340px;
  max-width: 100%;
  height: 38px;
  padding: 0 8px 0 10px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-left: 5px solid #bdbdbd;
  border-radius: 6px;
  outline: none;
  cursor: pointer;
  text-align: left;
  line-height: 36px;
  overflow: hidden;
  white-space: nowrap;
}
.stop-groups__bus:hover {
  border-color: #a5d6a7;
}
.stop-groups__bus-title {
  font-size: 16px;
  font-weight: 700;
  color: #212121;
  flex-shrink: 0;
}
.stop-groups__bus-sub {
  font-size: 12px;
  color: #757575;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.stop-groups__bus .v-icon {
  align-self: center;
}
.stop-groups__bus-item-dot {
  min-width: 24px !important;
}
.stop-groups__bus-item--active {
  background: #e8f5e9;
}
.stop-groups__search {
  max-width: 240px;
}
.stop-groups__dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
}
.stop-groups__hint {
  margin-bottom: 8px;
}
.stop-groups__empty {
  text-align: center;
  padding: 48px 0;
  color: #9e9e9e;
}
.stop-groups__grid {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 12px;
  align-items: start;
}
.stop-groups__cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
  align-items: stretch;
}
.stop-card {
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.stop-card--none {
  position: sticky;
  top: 0;
  align-self: start;
  min-height: 160px;
  border-top: 3px solid #bdbdbd;
}
.stop-card--warn {
  border-top-color: #ef6c00;
}
.stop-card__head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 8px 2px 8px;
}
.stop-card__handle {
  cursor: grab;
}
.stop-card__badge {
  min-width: 24px;
  height: 24px;
  border-radius: 5px;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  flex-shrink: 0;
}
.stop-card__badge--none {
  background: #9e9e9e;
}
.stop-card__title {
  font-weight: 600;
}
.stop-card__name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  font-size: 13px;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 2px 4px;
  outline: none;
}
.stop-card__name:hover,
.stop-card__name:focus {
  border-color: #bdbdbd;
  background: #fafafa;
}
.stop-card__count {
  font-size: 12px;
  color: #616161;
  background: #eeeeee;
  border-radius: 10px;
  padding: 1px 8px;
}
.stop-card__sub {
  padding: 0 10px 4px 40px;
}
.stop-card__body {
  flex: 1 1 auto;
  min-height: 56px;
  padding: 4px 8px 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.stop-student {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: grab;
  background: #fafafa;
}
.stop-student:hover {
  border-color: #90caf9;
}
.stop-student--match {
  background: #fff8e1;
  border-color: #ffb300;
}
.stop-student__name {
  font-size: 13px;
  font-weight: 500;
}
.stop-student__meta {
  font-size: 11px;
  color: #757575;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sortable-ghost {
  opacity: 0.4;
}
</style>
